import React from 'react';
import { Button, Card, Col, Image, Row, Space, Tabs, Tag, Typography } from 'antd';
import Link from 'antd/es/typography/Link';
import Paragraph from 'antd/es/typography/Paragraph';
import Title from 'antd/es/typography/Title';

import { CloudUploadOutlined, LinkOutlined, MailOutlined } from '@ant-design/icons';

import SnapTradeImport from '../../components/snaptrade/SnapTradeImport';
import UploadBox from '../../components/upload-box';
// eslint-disable-next-line camelcase -- Reason: legacy suppression retained until the owning code is migrated or cleaned up.
import DAS_Logo from '../../resources/images/DAS_Logo.png';
import { useAccountStore, useManagementStore, useUserState } from '../../store';

import './styles.scss';

const { Text } = Typography;

const DasInstruction = (
    <div>
        <Image
            // eslint-disable-next-line camelcase -- Reason: legacy suppression retained until the owning code is migrated or cleaned up.
            src={DAS_Logo}
            preview={false}
            height={30}
        />
        <Title level={4}>Supported Asset Types:</Title>
        <Tag color="processing">Stocks</Tag>
        <Tag color="processing">Options</Tag>
        <Title level={4}>How to Import Trades from DAS Trader:</Title>
        <Paragraph>
            To import trades from DAS Trader Pro, follow these steps:
            <ol>
                <li>Login to your DAS Trader Pro desktop platform.</li>
                <li>
                    Click on the Trades Window and select the following columns for your export:
                    Time, Symb, Side, Price, Qty, Route, Broker, Account, Type, Cloid.
                </li>
                <li>
                    If trading options: Right-click on the option chain window, then select
                    &quot;Config&quot; {'>'} and check &quot;Use Long Symbol&quot;.
                </li>
                <li>Right-click and export your trades into a CSV file from the Trades Window.</li>
                <li>
                    On the import window, select the date of the trades you are importing. You must
                    select a date because the data exported from DAS Trader Pro does not include
                    trade dates.
                </li>
                <li>Click &quot;Upload file &quot; to import your trades.</li>
            </ol>
        </Paragraph>
        <Title level={4}>Notes:</Title>
        <Paragraph>
            <ul>
                <li>DAS Trader only lets you export trades taken on the same day.</li>
                <li>
                    We recommend exporting your trades as soon as you are done trading for the day.
                </li>
            </ul>
        </Paragraph>
        <Title level={4}>Tutorial:</Title>
        <Link
            href="https://player.vimeo.com/video/1002080275?title=0&byline=0&portrait=0&badge=0&autopause=0&app_id=58479"
            target="_blank"
        >
            Watch Video
        </Link>
    </div>
);

export default function AddTradePage() {
    const { allAccounts, setSelectedAccount } = useAccountStore((state) => state);

    const { getUser } = useUserState();
    const user = getUser();
    const { viewAs, setViewAs } = useManagementStore((state) => state);

    const isViewAsActive =
        JSON.stringify(viewAs?.newUser?.token || null) !==
        JSON.stringify(viewAs?.mainUser?.token || null);

    if (isViewAsActive)
        setViewAs({ token: user?.token, id: user?.id }, { token: user?.token, id: user?.id });

    const handleSnapTradeSuccess = (selectedAccount) => {
        if (selectedAccount) {
            setSelectedAccount(selectedAccount);
        }
    };

    const items = [
        {
            key: '1',
            label: (
                <span>
                    <LinkOutlined />
                    Import Trades Automatically
                </span>
            ),
            children: (
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={12}>
                        <Card>
                            <Title level={4}>Connect & Import</Title>
                            <SnapTradeImport onSuccess={handleSnapTradeSuccess} />
                        </Card>
                    </Col>
                    <Col xs={24} lg={12} style={{ height: '80vh', overflowY: 'auto' }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Card>
                                <Title level={4}>Supported Brokers</Title>
                                <Paragraph>
                                    Connect your brokerage account to automatically import trades.
                                    We support major brokers through our secure partner SnapTrade.
                                </Paragraph>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '8px',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Card
                                        size="small"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <img
                                            src="https://www.interactivebrokers.com/images/common/logos/ibkr/interactive-brokers.svg"
                                            alt="Interactive Brokers"
                                            style={{
                                                width: '100%',
                                                height: '32px',
                                                marginBottom: '4px',
                                                objectFit: 'contain'
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'block';
                                            }}
                                        />
                                    </Card>
                                    <Card
                                        size="small"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <img
                                            src="https://imgs.search.brave.com/GyGt2WunqD5ZstPo6lUr2v-phFvJOLWT0TkSHA6tnxo/rs:fit:500:0:0:0/g:ce/aHR0cHM6Ly91cGxv/YWQud2lraW1lZGlh/Lm9yZy93aWtpcGVk/aWEvY29tbW9ucy90/aHVtYi80LzRiL0No/YXJsZXNfU2Nod2Fi/X0NvcnBvcmF0aW9u/X2xvZ28uc3ZnLzI1/MHB4LUNoYXJsZXNf/U2Nod2FiX0NvcnBv/cmF0aW9uX2xvZ28u/c3ZnLnBuZw"
                                            alt="schwab"
                                            style={{
                                                width: '100%',
                                                height: '32px',
                                                marginBottom: '4px',
                                                objectFit: 'contain',
                                                color: '#000'
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'block';
                                            }}
                                        />
                                    </Card>
                                    <Card
                                        size="small"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <img
                                            src="https://www.questrade.com/images/librariesprovider11/default-album/logos/questrade-logo-desktop.svg"
                                            alt="Questrade"
                                            style={{
                                                width: '100%',
                                                height: '32px',
                                                marginBottom: '4px',
                                                objectFit: 'contain'
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'block';
                                            }}
                                        />
                                    </Card>
                                    <Card
                                        size="small"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <img
                                            src="https://cdn2.etrade.net/1/22060112050.0/aempros/content/dam/etrade/retail/en_US/images/global/logos/etrade-from-MS.svg"
                                            alt="E*TRADE"
                                            style={{
                                                width: '100%',
                                                height: '22px',
                                                marginBottom: '4px',
                                                objectFit: 'contain'
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'block';
                                            }}
                                        />
                                    </Card>
                                    <Card
                                        size="small"
                                        style={{
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: '#f5f5f5'
                                        }}
                                    >
                                        <div style={{ fontSize: '20px', marginBottom: '4px' }}>
                                            ➕
                                        </div>
                                        <Text
                                            strong
                                            style={{ fontSize: '10px', lineHeight: '1.2' }}
                                        >
                                            {' '}
                                            Many More
                                        </Text>
                                    </Card>
                                </div>
                            </Card>

                            <Card>
                                <Title level={4}>How to Connect Your Broker</Title>
                                <Paragraph>
                                    Follow these steps to securely link your brokerage account and
                                    start automatic trade importing:
                                </Paragraph>
                                <ol>
                                    <li>Select your broker from the supported list above</li>
                                    <li>
                                        Click &quot;Connect Account&quot; and follow the secure
                                        authentication process
                                    </li>
                                    <li>Choose which Journal account to import trades into</li>
                                    <li>Select the date range for trade import</li>
                                    <li>Review and import your trades automatically</li>
                                </ol>

                                {/* <Title level={5} style={{ marginTop: '16px' }}>Tutorial Video</Title>
                          <Link
                              href='https://player.vimeo.com/video/1002080275?title=0&byline=0&portrait=0&badge=0&autopause=0&app_id=58479'
                              target='_blank'
                          >
                              Watch Video Tutorial →
                          </Link> */}
                            </Card>

                            <Card>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div>
                                        <Title level={4}>Don&apos;t see your broker?</Title>
                                        <Paragraph>
                                            We&apos;re continuously adding support for more brokers.
                                            Let us know which broker you&apos;d like us to add next.
                                        </Paragraph>
                                    </div>
                                    <Button
                                        type="primary"
                                        icon={<MailOutlined />}
                                        onClick={() => {
                                            const subject = encodeURIComponent(
                                                'Broker Request for Journal'
                                            );
                                            const body = encodeURIComponent(
                                                'Please add support for the following broker: [Broker Name]'
                                            );
                                            window.location.href = `mailto:mike@peakcapitaltrading.com?subject=${subject}&body=${body}`;
                                        }}
                                    >
                                        Request a Broker
                                    </Button>
                                </Space>
                            </Card>
                        </Space>
                    </Col>
                </Row>
            )
        },
        {
            key: '2',
            label: (
                <span>
                    <CloudUploadOutlined />
                    File Upload
                </span>
            ),
            children: (
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={10}>
                        {allAccounts?.length && <UploadBox />}
                    </Col>
                    <Col xs={24} lg={14}>
                        {DasInstruction}
                    </Col>
                </Row>
            )
        }
    ];

    return (
        <Space className="add-trade" direction="vertical" style={{ width: '100%' }}>
            <Card>
                <Tabs defaultActiveKey="2" items={items.filter((item) => item.key !== '1')} />
            </Card>
        </Space>
    );
}
